import React, { useState, useEffect, useCallback } from "react";
import {
  SafText,
  SafTab,
  SafTabs,
  SafTabPanel,
  SafButton,
  SafIcon,
  SafCard,
} from "@saffron/core-components/react";
import { useParams, useNavigate } from "react-router-dom";
import "../../../css/breadcrumb.css";
import "../../../css/common.css";
import {
  SafDialogbox,
  SupportingPatternTable,
  Integrations,
  Actors,
  Custom,
  CustomHeader,
} from "../../../shared";
import { convertTimeToMinutes } from "../../../util/index";

const ArchitectureDetails = () => {
  // Get assetId from URL parameters
  const { assetId: urlAssetId } = useParams();
  const navigate = useNavigate();

  const databaseId = process.env.REACT_APP_DB_SOURCE;
  const architectureDatabase = process.env.REACT_APP_DB_TARGET;

  const [disasterDetails, setDisasterDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openAlertOptions, setOpenAlertOptions] = useState(undefined);
  const [alertMessage, setAlertMessage] = useState("");

  const [isDiagramOpen, setIsDiagramOpen] = useState(false);
  const [isDecisionRecordOpen, setIsDecisionRecordOpen] = useState(false);
  const [isRoadMapOpen, setIsRoadMapOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Supporting Documents");
  const [supportingRefreshKey, setSupportingRefreshKey] = useState(0);

  const handleOpenDialog = (setOpen) => () => setOpen(true);
  const handleCloseDialog = (setOpen) => () => setOpen(false);

  // Middleware API endpoint
  const apiEndpoint = process.env.REACT_APP_MIDDLEWARE_ENDPOINT;

  // Fetch asset information using assetId from URL
  useEffect(() => {
    const fetchAssetNameById = async (id) => {
      if (!id) return;

      try {
        const operation = "get";
        const endpoint = `${apiEndpoint}&operation=${operation}`;

        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            dbName: databaseId,
            containerName:
              process.env.REACT_APP_CONTAINER_ASSET_INSIGHT_DATASOURCE,
            query: `SELECT c.id, c.asset_name FROM c WHERE c.id = '${id}'`,
          }),
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        if (data && data.length > 0) {
          const essentialInfo = {
            id: data[0].id,
            asset_name: data[0].asset_name,
          };
          // Update both state and localStorage
          localStorage.setItem("assetInfo", JSON.stringify(essentialInfo));
        }
      } catch (error) {
        console.error("Error fetching asset name:", error);
      }
    };

    if (urlAssetId) {
      // Always fetch fresh data when URL changes
      fetchAssetNameById(urlAssetId);
    } else if (localStorage.getItem("assetInfo")) {
      // If no URL param but we have localStorage data, redirect
      const storedInfo = JSON.parse(localStorage.getItem("assetInfo") || "{}");
      if (storedInfo.id) {
        navigate(`/${storedInfo.id}/architecture-details`, { replace: true });
      } else {
        // No valid data, go to home
        navigate("/home");
      }
    } else {
      // No data at all, go to home
      navigate("/home");
    }
  }, [urlAssetId, navigate, apiEndpoint, databaseId]);

  const getDisasterDetails = useCallback(async () => {
    if (!urlAssetId) return;

    setIsLoading(true);
    const containerId = process.env.REACT_APP_CONTAINER_FUSION;

    try {
      // Using middleware API for getting disaster details
      const operation = "get";
      const endpoint = `${apiEndpoint}&operation=${operation}`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName: databaseId,
          containerName: containerId,
          query: `SELECT * FROM c WHERE c.id = '${urlAssetId}'`,
          partitionKey: urlAssetId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // If data is a single object, wrap it in an array
      const items = Array.isArray(data) ? data : [data];
      const filteredItems = items.filter((item) => item?.id === urlAssetId);
      setDisasterDetails(filteredItems);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [databaseId, urlAssetId, apiEndpoint]);

  useEffect(() => {
    if (urlAssetId) {
      getDisasterDetails();
    }
  }, [getDisasterDetails, urlAssetId]);

  // Helper function to find an empty entry in an array
  const findEmptyEntry = (array, nameField, linkField) => {
    if (!array || !Array.isArray(array)) return -1;

    return array.findIndex(
      (item) =>
        (!item[nameField] || item[nameField] === "") &&
        (!item[linkField] || item[linkField] === "")
    );
  };

  const handleSubmitDiagram = async (formData) => {
    const supportingDiagramContainerID =
      process.env.REACT_APP_CONTAINER_ASSET_PARTITION;

    const newDiagramData = {
      diagram_name: formData?.diagramName,
      hyperlink: formData?.hyperlink,
    };

    try {
      // First get the existing document if it exists
      const getOperation = "get";
      const getEndpoint = `${apiEndpoint}&operation=${getOperation}`;

      const getResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName: architectureDatabase,
          containerName: supportingDiagramContainerID,
          query: `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'architecture-details'`,
          partitionKey: urlAssetId,
        }),
      });

      if (!getResponse.ok) {
        throw new Error(
          `Error getting existing data: ${getResponse.status} ${getResponse.statusText}`
        );
      }

      let existingData = await getResponse.json();

      // Handle if the response is an array
      if (Array.isArray(existingData) && existingData.length > 0) {
        existingData = existingData[0];
      }

      let documentData;

      // Handle document creation or update
      if (existingData && existingData.id === "architecture-details") {
        // Update existing document - preserve all fields
        documentData = { ...existingData };

        // Initialize arch_diags if it doesn't exist
        if (!documentData.arch_diags) {
          documentData.arch_diags = [];
        }

        // Check for empty entries
        const emptyIndex = findEmptyEntry(
          documentData.arch_diags,
          "diagram_name",
          "hyperlink"
        );

        if (emptyIndex !== -1) {
          // Update the empty entry instead of adding a new one
          documentData.arch_diags[emptyIndex] = newDiagramData;
        } else {
          // No empty entry found, add a new one
          documentData.arch_diags.push(newDiagramData);
        }
      } else {
        // Create new document
        documentData = {
          id: "architecture-details",
          asset_id: urlAssetId,
          arch_diags: [newDiagramData],
          adrs: [],
          road_map: [],
          integrations: [],
          actors: [],
          comments: [],
        };
      }

      // Now insert/update the document
      const insertOperation = "insert";
      const insertEndpoint = `${apiEndpoint}&operation=${insertOperation}`;

      const insertResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName: architectureDatabase,
          containerName: supportingDiagramContainerID,
          jsonData: documentData,
          partitionKey: urlAssetId,
        }),
      });

      if (!insertResponse.ok) {
        throw new Error(
          `Error inserting data: ${insertResponse.status} ${insertResponse.statusText}`
        );
      }

      setAlertMessage("Diagram was successfully added");
      setOpenAlertOptions(["toast", "success"]);
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 5000);
      setIsDiagramOpen(false);
      // Avoid full page reload; rely on local state updates or downstream components to refresh.
      setSupportingRefreshKey((k) => k + 1);
    } catch (err) {
      console.error(err);
      setAlertMessage("Failed to add diagram: " + err.message);
      setOpenAlertOptions(["toast", "error"]);
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 5000);
    }
  };

  const handleSubmitDecisionRecord = async (formData) => {
    const supportingDiagramContainerID =
      process.env.REACT_APP_CONTAINER_ASSET_PARTITION;

    const newDecisionData = {
      adr_name: formData?.decisionRecordName,
      hyperlink: formData?.hyperlink,
    };

    try {
      // First get the existing document if it exists
      const getOperation = "get";
      const getEndpoint = `${apiEndpoint}&operation=${getOperation}`;

      const getResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName: architectureDatabase,
          containerName: supportingDiagramContainerID,
          query: `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'architecture-details'`,
          partitionKey: urlAssetId,
        }),
      });

      if (!getResponse.ok) {
        throw new Error(
          `Error getting existing data: ${getResponse.status} ${getResponse.statusText}`
        );
      }

      let existingData = await getResponse.json();

      // Handle if the response is an array
      if (Array.isArray(existingData) && existingData.length > 0) {
        existingData = existingData[0];
      }

      let documentData;

      // Handle document creation or update
      if (existingData && existingData.id === "architecture-details") {
        // Update existing document - preserve all fields
        documentData = { ...existingData };

        // Initialize adrs if it doesn't exist
        if (!documentData.adrs) {
          documentData.adrs = [];
        }

        // Check for empty entries
        const emptyIndex = findEmptyEntry(
          documentData.adrs,
          "adr_name",
          "hyperlink"
        );

        if (emptyIndex !== -1) {
          // Update the empty entry instead of adding a new one
          documentData.adrs[emptyIndex] = newDecisionData;
        } else {
          // No empty entry found, add a new one
          documentData.adrs.push(newDecisionData);
        }
      } else {
        // Create new document
        documentData = {
          id: "architecture-details",
          asset_id: urlAssetId,
          arch_diags: [],
          adrs: [newDecisionData],
          road_map: [],
          integrations: [],
          actors: [],
          comments: [],
        };
      }

      // Now insert/update the document
      const insertOperation = "insert";
      const insertEndpoint = `${apiEndpoint}&operation=${insertOperation}`;

      const insertResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName: architectureDatabase,
          containerName: supportingDiagramContainerID,
          jsonData: documentData,
          partitionKey: urlAssetId,
        }),
      });

      if (!insertResponse.ok) {
        throw new Error(
          `Error inserting data: ${insertResponse.status} ${insertResponse.statusText}`
        );
      }

      setAlertMessage("Decision was successfully added");
      setOpenAlertOptions(["toast", "success"]);
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 5000);
      setIsDecisionRecordOpen(false);
      // Avoid full page reload; rely on local state updates or downstream components to refresh.
      setSupportingRefreshKey((k) => k + 1);
    } catch (err) {
      console.error(err);
      setAlertMessage("Failed to add decision record: " + err.message);
      setOpenAlertOptions(["toast", "error"]);
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 5000);
    }
  };

  const handleSubmitRoadMap = async (formData) => {
    const supportingDiagramContainerID =
      process.env.REACT_APP_CONTAINER_ASSET_PARTITION;

    const newRoadmapData = {
      road_map_name: formData?.roadMapName,
      hyperlink: formData?.hyperlink,
    };

    try {
      // First get the existing document if it exists
      const getOperation = "get";
      const getEndpoint = `${apiEndpoint}&operation=${getOperation}`;

      const getResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName: architectureDatabase,
          containerName: supportingDiagramContainerID,
          query: `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'architecture-details'`,
          partitionKey: urlAssetId,
        }),
      });

      if (!getResponse.ok) {
        throw new Error(
          `Error getting existing data: ${getResponse.status} ${getResponse.statusText}`
        );
      }

      let existingData = await getResponse.json();

      // Handle if the response is an array
      if (Array.isArray(existingData) && existingData.length > 0) {
        existingData = existingData[0];
      }

      let documentData;

      // Handle document creation or update
      if (existingData && existingData.id === "architecture-details") {
        // Update existing document - preserve all fields
        documentData = { ...existingData };

        // Initialize road_map if it doesn't exist
        if (!documentData.road_map) {
          documentData.road_map = [];
        }

        // Check for empty entries
        const emptyIndex = findEmptyEntry(
          documentData.road_map,
          "road_map_name",
          "hyperlink"
        );

        if (emptyIndex !== -1) {
          // Update the empty entry instead of adding a new one
          documentData.road_map[emptyIndex] = newRoadmapData;
        } else {
          // No empty entry found, add a new one
          documentData.road_map.push(newRoadmapData);
        }
      } else {
        // Create new document
        documentData = {
          id: "architecture-details",
          asset_id: urlAssetId,
          arch_diags: [],
          adrs: [],
          road_map: [newRoadmapData],
          integrations: [],
          actors: [],
          comments: [],
        };
      }

      // Now insert/update the document
      const insertOperation = "insert";
      const insertEndpoint = `${apiEndpoint}&operation=${insertOperation}`;

      const insertResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName: architectureDatabase,
          containerName: supportingDiagramContainerID,
          jsonData: documentData,
          partitionKey: urlAssetId,
        }),
      });

      if (!insertResponse.ok) {
        throw new Error(
          `Error inserting data: ${insertResponse.status} ${insertResponse.statusText}`
        );
      }

      setAlertMessage("Road map was successfully added");
      setOpenAlertOptions(["toast", "success"]);
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
      setIsRoadMapOpen(false);
      // Avoid full page reload; rely on local state updates or downstream components to refresh.
      setSupportingRefreshKey((k) => k + 1);
    } catch (err) {
      console.error(err);
      setAlertMessage("Failed to add road map: " + err.message);
      setOpenAlertOptions(["toast", "error"]);
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
    }
  };

  return (
    <div style={{ marginTop: "50px" }}>
      <CustomHeader
        headingText={"Architecture Details"}
        assetId={urlAssetId}
        archVersion={"1.0"}
      />
      <br />
      <div
        className="archtabs"
        style={{ marginTop: "95px", position: "relative" }}
      >
        <SafTabs
          // id="myTab"
          // orientation="horizontal"
          // onChange={(e) => {
          //   const tabName =
          //     e.detail.tabId === "Supporting Documents"
          //       ? "Supporting Documents"
          //       : "Integrations/Actors";
          //   setActiveTab(tabName);
          // }}
          id="myTab"
          orientation="horizontal"
          value={activeTab} // Add this line
          onChange={(e) => {
            setActiveTab(e.detail.tabId); // Simplified - just use the tabId directly
          }}
        >
          <SafTab
            id="Supporting Documents"
            onClick={() => setActiveTab("Supporting Documents")}
            style={{
              borderBottom:
                activeTab === "Supporting Documents" ? "3px solid #d64000" : "",
              fontWeight: activeTab === "Supporting Documents" ? "bold" : "",
              color: activeTab === "Supporting Documents" ? "#d64000" : "",
            }}
          >
            Supporting Documents
          </SafTab>
          <SafTab
            id="Integrations/Actors"
            onClick={() => setActiveTab("Integrations/Actors")}
            style={{
              borderBottom:
                activeTab === "Integrations/Actors" ? "3px solid #d64000" : "",
              fontWeight: activeTab === "Integrations/Actors" ? "bold" : "",
              color: activeTab === "Integrations/Actors" ? "#d64000" : "",
            }}
          >
            Integrations/Actors
          </SafTab>

          {/* Supporting Documents Tab Panel */}
          <SafTabPanel>
            <div className="dialog-header">
              <SafText appearance="body-small-md">
                Covers core architecture information allowing teams to link
                supporting documents, define their integrations and actors.
              </SafText>
              {/* Architecture Diagrams Section */}
              <div style={{ marginTop: "0px" }}>
                <h2 style={{ marginRight: "auto" }}>Architecture Diagrams</h2>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <SafText appearance="body-small-md">
                    Please add links and names of any architecture diagrams that
                    relate to this asset.
                  </SafText>
                  <SafButton
                    appearance="primary"
                    size="small"
                    onClick={handleOpenDialog(setIsDiagramOpen)}
                    style={{ marginLeft: "20px" }}
                  >
                    Add Diagram
                  </SafButton>
                </div>

                <SafDialogbox
                  openAlertOptions={openAlertOptions}
                  alertMessage={alertMessage}
                  isOpen={isDiagramOpen}
                  handleOnClose={handleCloseDialog(setIsDiagramOpen)}
                  handleCancelClick={handleCloseDialog(setIsDiagramOpen)}
                  handleSubmit={handleSubmitDiagram}
                  dialogTitle="Add Diagram"
                />
                <SupportingPatternTable
                  key={`diagram-${supportingRefreshKey}`}
                  name="add diagram"
                  assetId={urlAssetId}
                />
              </div>

              {/* Architecture Decision Records Section */}
              <div style={{ marginTop: "10px" }}>
                <h2 style={{ marginRight: "auto" }}>
                  Architect Decision Records
                </h2>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <SafText appearance="body-small-md">
                    Please add links and names of ADRs that relate to this
                    asset.
                  </SafText>
                  <SafButton
                    appearance="primary"
                    size="small"
                    onClick={handleOpenDialog(setIsDecisionRecordOpen)}
                    style={{ marginLeft: "20px" }}
                  >
                    Add Decision Records
                  </SafButton>
                </div>
                <SafDialogbox
                  openAlertOptions={openAlertOptions}
                  alertMessage={alertMessage}
                  isOpen={isDecisionRecordOpen}
                  handleOnClose={handleCloseDialog(setIsDecisionRecordOpen)}
                  handleCancelClick={handleCloseDialog(setIsDecisionRecordOpen)}
                  handleSubmit={handleSubmitDecisionRecord}
                  dialogTitle="Add Decision Record"
                />
                <SupportingPatternTable
                  key={`decision-${supportingRefreshKey}`}
                  name="add decision"
                  assetId={urlAssetId}
                />
              </div>

              {/* Application Road Maps Section */}
              <div style={{ marginTop: "10px" }}>
                <h2 style={{ marginRight: "auto" }}>Application Road Maps</h2>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <SafText appearance="body-small-md">
                    Please add links and names of Application Road Maps that
                    relate to this asset.
                  </SafText>
                  <SafButton
                    appearance="primary"
                    size="small"
                    onClick={handleOpenDialog(setIsRoadMapOpen)}
                    style={{ marginLeft: "20px" }}
                  >
                    Add Application road maps
                  </SafButton>
                </div>
                <SafDialogbox
                  openAlertOptions={openAlertOptions}
                  alertMessage={alertMessage}
                  isOpen={isRoadMapOpen}
                  handleOnClose={handleCloseDialog(setIsRoadMapOpen)}
                  handleCancelClick={handleCloseDialog(setIsRoadMapOpen)}
                  handleSubmit={handleSubmitRoadMap}
                  dialogTitle="Add Application Road Map"
                />
                <SupportingPatternTable
                  key={`roadmap-${supportingRefreshKey}`}
                  name="add roadmap"
                  assetId={urlAssetId}
                />
              </div>

              {/* Disaster Recovery Plan Section (moved from previous separate tab) */}
              <div style={{ marginTop: "10px" }}>
                <h2 style={{ marginRight: "auto" }}>Disaster Recovery Plan</h2>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <SafText appearance="body-small-md">
                    Provides links to the assets DRs plans and the current SLAs
                    required to be supported
                  </SafText>
                  <SafButton
                    appearance="secondary"
                    size="small"
                    onClick={() => window.location.reload()}
                    style={{ marginLeft: "20px" }}
                  >
                    <SafIcon
                      slot="start"
                      icon-name="refresh"
                      appearance="solid"
                    ></SafIcon>
                    Refresh Data
                  </SafButton>
                </div>
                <div style={{ maxWidth: "1600px", margin: "0" }}>
                  {isLoading ? (
                    <SafText>Loading...</SafText>
                  ) : disasterDetails.length === 0 ||
                    !disasterDetails.some(
                      (item) => item?.dr_plans?.length > 0
                    ) ? (
                    <SafText>
                      No disaster recovery plans available in Fusion
                    </SafText>
                  ) : (
                    disasterDetails?.map((item, index) =>
                      item?.dr_plans?.map((plan, planIndex) => (
                        <SafCard
                          key={`${index}-${planIndex}`}
                          style={{ marginBottom: "2px" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <div style={{ width: "75%" }}>
                              <SafText>
                                <strong>{plan?.dr_plan_name}</strong>
                              </SafText>
                              <br />
                              <SafText style={{ color: "grey" }}>
                                {plan?.dr_plan_description?.replace(
                                  /<[^>]*>/g,
                                  ""
                                )}
                              </SafText>
                            </div>
                            <div>
                              <SafText>
                                <strong>Recovery time objective (RTO)</strong>
                              </SafText>
                              <br />
                              <SafIcon
                                slot="start"
                                iconName="clock"
                                size="small"
                                color="grey"
                              />
                              <SafText style={{ color: "grey" }}>
                                {convertTimeToMinutes(plan?.rto)}
                                min
                              </SafText>
                              <br />
                              <SafText>
                                <strong>Recovery point objective (RPO)</strong>
                              </SafText>
                              <br />
                              <SafIcon
                                slot="start"
                                iconName="clock"
                                size="small"
                                color="grey"
                              />
                              <SafText style={{ color: "grey" }}>
                                {convertTimeToMinutes(plan?.rpo)}
                                min
                              </SafText>
                            </div>
                          </div>
                        </SafCard>
                      ))
                    )
                  )}
                </div>
              </div>
              <div style={{ marginTop: "10px" }}>
                {/* <div style={{ display: "flex", alignItems: "center" }}>
    <h2 style={{ marginRight: "auto" }}>Comments</h2>
  </div> */}
                <Custom activeTabName={activeTab} />
              </div>
            </div>
          </SafTabPanel>

          {/* Integrations/Actors Tab Panel */}
          <SafTabPanel>
            <div
              className="dialog-header"
              style={{ padding: "0px 25px 25px 25px" }}
            >
              <SafText appearance="body-small-md">
                Integrations are the data flows the asset requires to provide
                the service and what it depends on. Actors are the consumers or
                have a dependency on the service the asset supplies.
              </SafText>
              {/* Integrations Section */}
              <div>
                {/* <div style={{ display: "flex", alignItems: "center" }}>
                <h2 style={{ marginRight: "auto" }}>Integrations</h2>
              </div> */}
                <Integrations />
              </div>

              {/* Actors Section */}
              <div style={{ marginTop: "10px" }}>
                {/* <div style={{ display: "flex", alignItems: "center" }}>
                <h2 style={{ marginRight: "auto" }}>Actors</h2>
              </div> */}
                <Actors />
              </div>
              <div style={{ marginTop: "10px" }}>
                {/* <div style={{ display: "flex", alignItems: "center" }}>
    <h2 style={{ marginRight: "auto" }}>Comments</h2>
  </div> */}
                <Custom activeTabName={activeTab} />
              </div>
            </div>
          </SafTabPanel>
        </SafTabs>
      </div>
    </div>
  );
};

export default ArchitectureDetails;
